/**
 * BulkMisFilters Component
 *
 * Filter panel for bulk MIS dashboard
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, SearchBar } from 'equal-ds-ui';
import { X } from 'lucide-react';
import { Select } from '../../ui/Select';
import type { BulkMisFilters } from '../../../types/bulk-mis';
import { isBulkOrchestrationV2Enabled } from '../../../utils/feature-flags';

interface BulkMisFiltersProps {
  filters: BulkMisFilters;
  onFiltersChange: (filters: BulkMisFilters) => void;
  /** Controls V2-specific filter behavior. Only 'data_fetch' gets V2 gating. */
  screenName?: string;
}

const SEARCH_DEBOUNCE_MS = 500;

export const BulkMisFiltersComponent: React.FC<BulkMisFiltersProps> = ({
  filters,
  onFiltersChange,
  screenName,
}) => {
  const isV2 = screenName === 'data_fetch' && isBulkOrchestrationV2Enabled();
  // Local state for search input (for immediate UI feedback)
  const [searchValue, setSearchValue] = useState(filters.search);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local search value when filters.search changes externally (e.g., reset)
  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to update filters after debounce delay
    debounceTimerRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, search: value });
    }, SEARCH_DEBOUNCE_MS);
  }, [filters, onFiltersChange]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleStatusChange = (value: string) => {
    onFiltersChange({ ...filters, status: value });
  };

  const handleBatchTypeChange = (value: string) => {
    onFiltersChange({ ...filters, batchType: value });
  };

  const handlePipelineSourceChange = (value: string) => {
    onFiltersChange({ ...filters, pipelineSource: value });
  };

  const handleReset = () => {
    onFiltersChange({
      search: '',
      status: 'all',
      dateRange: { from: null, to: null },
      pipelineSource: 'all',
      batchType: 'all',
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.status !== 'all' ||
    (isV2 && filters.batchType !== 'all') ||
    (!isV2 && screenName === 'data_fetch' && filters.pipelineSource !== 'all') ||
    filters.dateRange.from ||
    filters.dateRange.to;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <div className="flex flex-row gap-4 items-center">
        {/* Search */}
        <div className="flex-1">
          <SearchBar
            placeholder="Search by file name, batch ID..."
            value={searchValue}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>

        {/* Status Filter */}
        <div className="w-48">
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'IN_PROGRESS', label: 'In Progress' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'FAILED', label: 'Failed' },
            ]}
          />
        </div>

        {/* Type Filter - V2 data_fetch only */}
        {isV2 && (
          <div className="w-48">
            <Select
              value={filters.batchType}
              onChange={handleBatchTypeChange}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'loan_monitoring', label: 'Loan Monitoring' },
                { value: 'balance', label: 'Balance' },
              ]}
            />
          </div>
        )}

        {/* Pipeline Source Filter - V1 data_fetch only */}
        {!isV2 && screenName === 'data_fetch' && (
          <div className="w-48">
            <Select
              value={filters.pipelineSource}
              onChange={handlePipelineSourceChange}
              options={[
                { value: 'all', label: 'All Sources' },
                { value: 'api', label: 'API' },
                { value: 'portal', label: 'Portal' },
              ]}
            />
          </div>
        )}

        {/* Reset Button (only if filters are active) */}
        {hasActiveFilters && (
          <Button
            onClick={handleReset}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Reset
          </Button>
        )}
      </div>
    </div>
  );
};
